// AIT-639 — send the ChatGPT Ads `registration_completed` conversion once the
// freshly signed-up user is authenticated. Mounted once at the router root
// (OpenAIRegistrationMount in app.tsx, same shape as MetaOAuthCompletionMount)
// rather than on landing pages: a signup whose AuthKit returnTo is a protected
// deep link never renders RootRedirect or the invite flow.
//
// The pixel SDK is loaded lazily from here, not at startup: the marketing and
// sandbox handoffs return the new user to `/signup?handoff=<jwt>` /
// `/sandbox`, where the SDK must not run (credential in the URL). The effect
// re-evaluates on every route change and only initializes + fires once the
// session has moved to a safe path (`/`, `/w/:ws`, `/invite/connect`, ...).
// trackRegistration() gates on the server's isFreshSignup flag + a per-user
// localStorage flag, so re-runs are harmless.
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { initOpenAIPixel, isCredentialBearingPath, trackRegistration } from '@/lib/openai-pixel';

export function useTrackOpenAIRegistration(
  user: { id?: string; email?: string | null; isFreshSignup?: boolean } | null,
): void {
  const { pathname } = useLocation();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    if (!user?.id || !user.isFreshSignup) return;
    if (isCredentialBearingPath(pathname)) return;
    initOpenAIPixel();
    void trackRegistration({ id: user.id, email: user.email ?? null, isFreshSignup: true }).then((sent) => {
      if (sent) done.current = true;
    });
  }, [user?.id, user?.email, user?.isFreshSignup, pathname]);
}
